import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import React, { useEffect, useState, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "./hooks/useAuth";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import GlobalCookieBanner from "@/components/ui/global-cookie-banner";
import { ScriptManager } from "@/components/ScriptManager";
import { FooterVisibilityProvider, useFooterVisibility } from "@/contexts/FooterVisibility";
const SiteFooter = lazy(() => import("@/components/SiteFooter"));

/**
 * AppShell wraps the router slot and the global SiteFooter. SiteFooter renders
 * for every route by default; routes opt out via FOOTER_OPT_OUT_EXACT /
 * FOOTER_OPT_OUT_PREFIXES, and the 404 NotFound page suppresses it via
 * useSuppressGlobalFooter() since we can't detect "no Switch match" from path
 * alone. The outer container only applies `gratimy-dashboard-bg` when the
 * footer is shown, so non-app routes (landing, login, acceptance flows, 404)
 * keep their own background unchanged.
 */
const FOOTER_OPT_OUT_EXACT = new Set<string>([
  "/",
  "/login",
  "/reset-password",
  "/share-acceptance",
  "/ownership-transfer-acceptance",
  "/design-system",
]);
const FOOTER_OPT_OUT_PREFIXES = ["/journal/invite/"];

function AppShell({ location }: { location: string }) {
  const { suppressed } = useFooterVisibility();
  const optedOut =
    FOOTER_OPT_OUT_EXACT.has(location) ||
    FOOTER_OPT_OUT_PREFIXES.some((p) => location.startsWith(p));
  const showFooter = !optedOut && !suppressed;
  return (
    <div className="flex flex-col min-h-screen">
      <div
        className={`flex-1 flex flex-col${showFooter ? " gratimy-dashboard-bg" : ""}`}
      >
        <Router />
        {showFooter && (
          <Suspense fallback={null}>
            <SiteFooter />
          </Suspense>
        )}
      </div>
    </div>
  );
}

// === BUNDLE SIZE OPTIMIZATION - CODE SPLITTING STRATEGY ===
// To achieve our 157KB bundle target, we split pages into two groups:
// 1. EAGER LOAD: Pages needed immediately (login flow)
// 2. LAZY LOAD: Everything else loads on-demand

// === EAGER LOADED PAGES (INCLUDED IN MAIN BUNDLE) ===
// These 3 pages are critical for first-time visitors
// They're included in the initial 158KB bundle
import LandingPage from "@/pages/landing";     // Homepage - first thing users see
import LoginPage from "@/pages/login";         // Authentication - needed immediately
const Dashboard = lazy(() => import("@/pages/dashboard")); // Main app screen — lazy to keep initial bundle small

const OnboardingModal = lazy(() => import("@/components/OnboardingModal"));
const FeatureWalkthrough = lazy(() => import("@/components/FeatureWalkthrough"));

// === LAZY LOADED PAGES (SEPARATE CHUNKS) ===
// Each of these creates a separate JavaScript file
// Downloaded only when user navigates to that page
// This keeps our initial bundle small and fast
const NotFound = lazy(() => import("@/pages/not-found"));                   // 404 page (~5KB)
const CreateBook = lazy(() => import("@/pages/create-book"));               // Book creation (~25KB)
const EditBook = lazy(() => import("@/pages/edit-book"));                   // Book editing (~20KB)
const BookDetail = lazy(() => import("@/pages/book-carousel"));             // Book viewer (~30KB)
const AddGift = lazy(() => import("@/pages/add-gift"));                     // Gift form (~15KB)
const GiftTable = lazy(() => import("@/pages/gift-table"));                 // Gift list (~10KB)
const AppLandingPage = lazy(() => import("@/pages/app-landing"));           // Feature info (~20KB)
const PlansPage = lazy(() => import("@/pages/plans"));                      // Subscription plans (~10KB)
const PricingPage = lazy(() => import("@/pages/pricing"));                  // Pricing details (~8KB)
const PrivacyPolicyPage = lazy(() => import("@/pages/privacy-policy"));     // Legal pages (~5KB each)
const TermsPage = lazy(() => import("@/pages/terms"));                      // Terms of service
const ContactPage = lazy(() => import("@/pages/contact"));                  // Contact info
const SharedBooksPage = lazy(() => import("@/pages/shared-books"));         // Collaboration (~15KB)
const ShareAcceptancePage = lazy(() => import("@/pages/share-acceptance"));  // Share acceptance (~10KB)
const PaymentInfoPage = lazy(() => import("@/pages/payment-info"));         // Payment settings (~12KB)
const AccountSettingsPage = lazy(() => import("@/pages/account-settings")); // User settings (~10KB)
const PublicGiftEntry = lazy(() => import("@/pages/public-gift-entry"));    // Anonymous gifts (~18KB)
const CookieSettingsPage = lazy(() => import("@/pages/cookie-settings"));   // Cookie preferences (~8KB)
const CookiePolicyPage = lazy(() => import("@/pages/cookie-policy"));     // Cookie policy (~6KB)
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));   // Password reset (~10KB)
const OwnershipTransferAcceptancePage = lazy(() => import("@/pages/ownership-transfer-acceptance")); // Ownership transfer (~10KB)
const DesignSystemPage = lazy(() => import("@/pages/design-system"));                             // Design system reference (~40KB)
const GiftSuccessPage = lazy(() => import("@/pages/gift-success"));                              // Gift success (~8KB)
const ContributionsPage = lazy(() => import("@/pages/contributions"));                            // Sent gifts (~10KB)

// === LOADING STATE FOR CODE-SPLIT PAGES ===
// Shows while lazy-loaded chunks are downloading
// Typically appears for 100-300ms on fast connections
// Uses CSS animation (no JavaScript) to keep it lightweight
const RouteLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center gratimy-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
      <p className="text-white/70">Loading...</p>
    </div>
  </div>
);

// === PROTECTED ROUTE WRAPPER ===
// Ensures only logged-in users can access certain pages
// Also handles the Suspense boundary for lazy-loaded protected pages
function ProtectedRoute({ component: Component, ...rest }: { component: React.ComponentType<any>, [key: string]: any }) {
  const { user, session, loading } = useAuth();
  const [, setLocation] = useLocation();

  // === AUTHENTICATION CHECK ===
  // Wait for auth to load, then redirect if not logged in
  useEffect(() => {
    // CRITICAL: Check BOTH user AND session
    if (!loading && (!user || !session)) {
      setLocation("/login");  // Send to login page
    }
  }, [loading, user, session, setLocation]);

  // === LOADING STATES ===
  // Show spinner while checking if user is logged in
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center gratimy-background">
      <div className="loader animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
    </div>;
  }

  // Show redirect message if not authenticated
  // CRITICAL: Check BOTH user AND session
  if (!user || !session) {
    return <div className="min-h-screen flex items-center justify-center gratimy-background">
      <div className="text-center">
        <p className="text-white/80 mb-2">Redirecting to login...</p>
        <div className="loader animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mx-auto"></div>
      </div>
    </div>;
  }

  // === RENDER PROTECTED COMPONENT ===
  // Component is wrapped in Suspense for lazy loading
  return (
    <Suspense fallback={<RouteLoader />}>
      <Component {...rest} />
    </Suspense>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} /> {/* New landing page as the homepage */}
      <Route path="/login" component={LoginPage} />
      <Route path="/plans" component={PlansPage} />
      <Route path="/pricing">
        <Suspense fallback={<RouteLoader />}>
          <PricingPage />
        </Suspense>
      </Route>
      <Route path="/dashboard" component={() => <ProtectedRoute component={Dashboard} />} />
      <Route path="/app-info" component={() => <ProtectedRoute component={AppLandingPage} />} />
      <Route path="/create-book" component={() => <ProtectedRoute component={CreateBook} />} />
      <Route path="/edit-book/:id">
        {(params) => <ProtectedRoute component={EditBook} id={params.id} />}
      </Route>
      <Route path="/book/:id">
        {(params) => <ProtectedRoute component={BookDetail} id={params.id} />}
      </Route>
      <Route path="/book/:id/add-gift">
        {(params) => <ProtectedRoute component={AddGift} id={params.id} />}
      </Route>
      <Route path="/gift-table/:journalId/:occasionId">
        {(params) => <ProtectedRoute component={GiftTable} journalId={params.journalId} occasionId={params.occasionId} />}
      </Route>
      <Route path="/shared-books" component={() => <ProtectedRoute component={SharedBooksPage} />} />
      <Route path="/payment-info" component={() => <ProtectedRoute component={PaymentInfoPage} />} />
      <Route path="/account" component={() => <ProtectedRoute component={AccountSettingsPage} />} />
      <Route path="/contributions" component={() => <ProtectedRoute component={ContributionsPage} />} />
      {/* Public legal and info pages */}
      <Route path="/privacy-policy">
        <Suspense fallback={<RouteLoader />}>
          <PrivacyPolicyPage />
        </Suspense>
      </Route>
      <Route path="/terms">
        <Suspense fallback={<RouteLoader />}>
          <TermsPage />
        </Suspense>
      </Route>
      <Route path="/contact">
        <Suspense fallback={<RouteLoader />}>
          <ContactPage />
        </Suspense>
      </Route>
      <Route path="/cookie-settings">
        <Suspense fallback={<RouteLoader />}>
          <CookieSettingsPage />
        </Suspense>
      </Route>
      <Route path="/cookie-policy">
        <Suspense fallback={<RouteLoader />}>
          <CookiePolicyPage />
        </Suspense>
      </Route>
      {/* Public gift entry route */}
      <Route path="/journal/invite/:token">
        {(params) => (
          <Suspense fallback={<RouteLoader />}>
            <PublicGiftEntry />
          </Suspense>
        )}
      </Route>
      {/* Share acceptance page */}
      <Route path="/share-acceptance">
        <Suspense fallback={<RouteLoader />}>
          <ShareAcceptancePage />
        </Suspense>
      </Route>
      {/* Password reset page */}
      <Route path="/reset-password">
        <Suspense fallback={<RouteLoader />}>
          <ResetPasswordPage />
        </Suspense>
      </Route>
      {/* Ownership transfer acceptance page */}
      <Route path="/ownership-transfer-acceptance">
        <Suspense fallback={<RouteLoader />}>
          <OwnershipTransferAcceptancePage />
        </Suspense>
      </Route>
      <Route path="/gift-success">
        <Suspense fallback={<RouteLoader />}>
          <GiftSuccessPage />
        </Suspense>
      </Route>
      <Route path="/design-system">
        <Suspense fallback={<RouteLoader />}>
          <DesignSystemPage />
        </Suspense>
      </Route>
      {/* Fallback to 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function OnboardingGate() {
  const { user } = useAuth();
  const { data: profile } = useQuery<{ firstName?: string; lastName?: string; country?: string; streetAddress?: string; city?: string; postalCode?: string; currencyPreference?: string }>({
    queryKey: ['/api/user/profile'],
    enabled: !!user,
  });

  const needsOnboarding = !!user && !!profile && (
    !profile.firstName || !profile.lastName || !profile.country ||
    !profile.streetAddress || !profile.city || !profile.postalCode ||
    !profile.currencyPreference
  );

  if (!needsOnboarding) return null;

  return (
    <Suspense fallback={null}>
      <OnboardingModal />
    </Suspense>
  );
}

function WalkthroughGate() {
  const [tourRequested, setTourRequested] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem('gratimy-tour-pending') === 'true') {
        setTourRequested(true);
      }
    } catch {}

    const handler = () => setTourRequested(true);
    window.addEventListener('gratimy-start-tour', handler);
    return () => window.removeEventListener('gratimy-start-tour', handler);
  }, []);

  if (!tourRequested) return null;

  return (
    <Suspense fallback={null}>
      <FeatureWalkthrough />
    </Suspense>
  );
}

function TranslationGate({ children }: { children: React.ReactNode }) {
  const { translationsReady } = useLanguage();
  if (!translationsReady) {
    return (
      <div className="flex items-center justify-center min-h-screen gratimy-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }
  return <>{children}</>;
}

function AppWithProviders() {
  const { user, loading } = useAuth();
  const [location] = useLocation();

  const { data: userProfile } = useQuery<{ currencyPreference?: string; country?: string }>({
    queryKey: ['/api/user/profile', user?.id],
    enabled: !!user,
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen gratimy-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }
  
  return (
    <LanguageProvider>
      <TranslationGate>
        <CurrencyProvider userCountry={userProfile?.country ?? undefined} userCurrencyPreference={userProfile?.currencyPreference ?? undefined}>
          <CookieConsentProvider>
            <TooltipProvider>
              <Toaster />
              <FooterVisibilityProvider>
                <AppShell location={location} />
              </FooterVisibilityProvider>
              <GlobalCookieBanner />
              <ScriptManager />
              <OnboardingGate />
              <WalkthroughGate />
            </TooltipProvider>
          </CookieConsentProvider>
        </CurrencyProvider>
      </TranslationGate>
    </LanguageProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppWithProviders />
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
